import { ThemeEvents } from '@theme/events';

/**
 * Resolves a CDN image URL from a variant JSON object.
 * @param {object | null | undefined} variant
 * @param {number} [width=200]
 * @returns {string | null}
 */
export function resolveVariantImageSrc(variant, width = 200) {
  if (!variant) return null;

  const raw =
    variant.featured_media?.preview_image?.src ||
    variant.featured_image?.src ||
    null;

  if (!raw || typeof raw !== 'string') return null;

  try {
    const url = new URL(raw, window.location.origin);
    url.searchParams.set('width', String(width));
    return url.toString();
  } catch {
    const separator = raw.includes('?') ? '&' : '?';
    return `${raw}${separator}width=${width}`;
  }
}

/**
 * Displays the selected variant image and updates on variant:update.
 */
class VariantSelectedImage extends HTMLElement {
  /** @type {(event: Event) => void} */
  #onVariantUpdate = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (!detail) return;

    if (detail.data?.newProduct?.id) {
      this.dataset.productId = String(detail.data.newProduct.id);
    } else if (
      detail.data?.productId &&
      String(detail.data.productId) !== String(this.dataset.productId)
    ) {
      return;
    }

    const width = Number.parseInt(this.dataset.imageWidth || '200', 10) || 200;
    const nextSrc =
      resolveVariantImageSrc(detail.resource, width) || this.dataset.fallbackSrc || null;

    if (!nextSrc) return;

    const img = this.querySelector('img');
    if (!(img instanceof HTMLImageElement)) return;

    if (img.src !== nextSrc) {
      img.src = nextSrc;
      img.removeAttribute('srcset');
    }

    const alt = detail.resource?.featured_image?.alt || this.dataset.fallbackAlt;
    if (alt) img.alt = alt;
  };

  connectedCallback() {
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }
}

if (!customElements.get('variant-selected-image')) {
  customElements.define('variant-selected-image', VariantSelectedImage);
}
